// src/pages/MatchingRelaties.js
// vMR-005
//
// StaffIQ HRM - Matching Relaties
// Opzet gelijk aan Matching Personeel:
// - Links/boven: zoeken + knoppen
// - Midden: resultaten-grid met sortering en kolomfilters
// - Rechts: selectiecriteria
// - Popup: relatiekaart bekijken/wijzigen/opslaan/printen intern/extern

import React, { useEffect, useMemo, useRef, useState } from "react";
import useVoiceCommands from "../components/useVoiceCommands";

const VERSION = "vMR-005";

const buildUrl = (u) =>
  u.startsWith("http") ? u : `${u.startsWith("/") ? u : `/${u}`}`;

function norm(s) {
  return String(s || "")
    .toLowerCase()
    .trim()
    .replace(/[，、]/g, ",")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/[;:!?]/g, "")
    .replace(/\s+/g, " ");
}

function safeTrim(s) {
  return String(s || "").trim();
}

function uniqSorted(arr) {
  const set = new Set((arr || []).map((x) => String(x).trim()).filter(Boolean));
  return Array.from(set).sort((a, b) => a.localeCompare(b, "nl"));
}

function isoDate(s) {
  const t = String(s || "").slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(t) ? t : "";
}

function formatDateNL(iso) {
  const s = String(iso || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return "";
  const [y, m, d] = s.split("-");
  return `${d}-${m}-${y}`;
}

function parseDecimal(v) {
  const n = Number(String(v ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

function formatMoney(val) {
  const n = Number(val || 0);
  if (!Number.isFinite(n)) return "";
  return n.toLocaleString("nl-NL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function parseActiveValue(it) {
  const raw = it?.active ?? it?.is_active ?? it?.isActive ?? it?.actief;
  if (raw === undefined || raw === null || raw === "") return null;
  if (raw === true) return true;
  if (raw === false) return false;
  const s = String(raw).toLowerCase().trim();
  if (raw === 1 || s === "1" || s === "true" || s === "yes" || s === "y" || s === "j" || s === "ja" || s === "aan" || s === "on") return true;
  if (raw === 0 || s === "0" || s === "false" || s === "no" || s === "n" || s === "nee" || s === "uit" || s === "off") return false;
  return !!raw;
}

function activeToBool(it) {
  const v = parseActiveValue(it);
  return v === null ? true : !!v;
}

function isEmptyWord(t) {
  const x = norm(t);
  return x === "geen" || x === "blanco" || x === "leeg" || x === "null";
}

function textIncludes(source, needle) {
  const n = norm(needle);
  if (!n) return true;
  return norm(source).includes(n);
}

function boolMatches(value, wanted) {
  const w = norm(wanted);
  if (!w) return true;
  const v = value === true ? "j ja true 1" : "n nee false 0";
  return v.includes(w);
}

function relationTypeLabel(it) {
  const v = String(it?.relation_entity_type || "company").toLowerCase();
  return v === "private" ? "Particulier" : "Organisatie";
}

function relationTypeShort(it) {
  const v = String(it?.relation_entity_type || "company").toLowerCase();
  return v === "private" ? "P" : "O";
}

function displayName(it) {
  const isPrivate = String(it?.relation_entity_type || "").toLowerCase() === "private";

  if (!isPrivate) {
    return safeTrim(it?.search_name || it?.name || "");
  }

  const first = safeTrim(it?.first_name || it?.initials || "");
  const prefix = safeTrim(it?.name_prefix || "");
  const last = safeTrim(it?.last_name || "");
  const normal = [first, prefix, last].filter(Boolean).join(" ");
  return safeTrim(it?.search_name || normal || it?.name || "");
}

function companyCodeName(it = {}) {
  return safeTrim(it.company_code_name || it.company_code || it.company_code_display || "");
}

function companyCodeIdValue(it = {}) {
  const raw = it.company_code_id ?? it.companyCodeId ?? "";
  return raw === null || raw === undefined ? "" : String(raw);
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function toIntString(v) {
  return String(v || "").replace(/[^\d]/g, "");
}

async function tryFetchJson(urls, options = {}) {
  const token =
    sessionStorage.getItem("token") ||
    localStorage.getItem("token") ||
    "";

  const tenant =
    sessionStorage.getItem("tenant") ||
    localStorage.getItem("tenant") ||
    "demo";

  let userObj = {};
  let role = "user";
  try {
    userObj = JSON.parse(
      sessionStorage.getItem("user") ||
      localStorage.getItem("user") ||
      "{}"
    ) || {};
    role = userObj?.role || "user";
  } catch {
    userObj = {};
  }

  let lastErr = null;

  for (const url of urls) {
    try {
      const headers = {
        ...(options.headers || {}),
        Authorization: token ? `Bearer ${token}` : undefined,
        "x-tenant": tenant,
        "x-role": role,
        "x-session-key": userObj?.session_key || userObj?.sessionKey || undefined,
        "x-staffiq-user-id": userObj?.id || userObj?.user_id || undefined,
        "x-staffiq-user-email": userObj?.email || undefined,
        "x-staffiq-user-name": userObj?.display_name || userObj?.name || undefined,
      };

      Object.keys(headers).forEach(
        (k) => headers[k] === undefined && delete headers[k]
      );

      const res = await fetch(buildUrl(url), {
        ...options,
        headers,
      });

      if (!res.ok) {
        const raw = await res.text();
        let msg = `${url} (${res.status})`;

        try {
          const data = raw ? JSON.parse(raw) : null;
          msg = data?.error || data?.message || raw || msg;
        } catch {
          msg = raw || msg;
        }

        throw new Error(msg);
      }

      if (res.status === 204) return null;
      const text = await res.text();
      if (!text) return null;

      try {
        return JSON.parse(text);
      } catch {
        return text;
      }
    } catch (err) {
      lastErr = err;
    }
  }

  throw lastErr;
}

function normalizePhoneForCommunication(raw, defaultCountryCode = "+31") {
  let s = String(raw || "").trim();

  if (!s || s === "-") return "";

  s = s
    .replace(/[^\d+]/g, "")
    .replace(/^00/, "+");

  if (!s) return "";

  if (s.startsWith("+")) return s;

  if (s.startsWith("0")) {
    return `${defaultCountryCode}${s.slice(1)}`;
  }

  if (s.startsWith("31")) {
    return `+${s}`;
  }

  return `${defaultCountryCode}${s}`;
}

async function loadCommunicationSettingsSafe() {
  try {
    const data = await tryFetchJson(["/api/communication-settings"]);

    return {
      email_enabled: data?.email_enabled !== false,
      whatsapp_enabled: data?.whatsapp_enabled !== false,
      sms_enabled: data?.sms_enabled === true,
      default_country_code: data?.default_country_code || "+31",
      default_sms_text: data?.default_sms_text || "",
      default_whatsapp_text: data?.default_whatsapp_text || "",
    };
  } catch {
    return {
      email_enabled: true,
      whatsapp_enabled: true,
      sms_enabled: false,
      default_country_code: "+31",
      default_sms_text: "",
      default_whatsapp_text: "",
    };
  }
}

function stopGridClick(e) {
  try {
    e.preventDefault();
    e.stopPropagation();
  } catch {}
}

function askCommunicationChoice(raw) {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");

    overlay.style.position = "fixed";
    overlay.style.left = "0";
    overlay.style.top = "0";
    overlay.style.right = "0";
    overlay.style.bottom = "0";
    overlay.style.background = "rgba(0,0,0,0.35)";
    overlay.style.zIndex = "999999";
    overlay.style.display = "flex";
    overlay.style.alignItems = "center";
    overlay.style.justifyContent = "center";
    overlay.style.padding = "18px";
    overlay.style.boxSizing = "border-box";

    const box = document.createElement("div");

    box.style.width = "min(520px, 100%)";
    box.style.background = "#fff";
    box.style.borderRadius = "12px";
    box.style.boxShadow = "0 18px 50px rgba(0,0,0,0.35)";
    box.style.padding = "18px";
    box.style.boxSizing = "border-box";
    box.style.fontFamily = "Arial, Helvetica, sans-serif";
    box.style.color = "#111";

    const title = document.createElement("div");
    title.style.fontWeight = "700";
    title.style.fontSize = "18px";
    title.style.marginBottom = "12px";
    title.textContent = `Wat wil je doen met ${raw}?`;

    const help = document.createElement("div");
    help.style.fontSize = "16px";
    help.style.lineHeight = "1.8";
    help.style.marginBottom = "12px";
    help.innerHTML = "1 = Bellen<br/>2 = SMS<br/>3 = WhatsApp";

    const input = document.createElement("input");
    input.type = "text";
    input.value = "3";
    input.inputMode = "numeric";
    input.autocomplete = "off";
    input.style.width = "100%";
    input.style.fontSize = "20px";
    input.style.padding = "10px 12px";
    input.style.border = "2px solid #1f4e79";
    input.style.borderRadius = "8px";
    input.style.boxSizing = "border-box";
    input.style.marginBottom = "14px";

    const buttons = document.createElement("div");
    buttons.style.display = "flex";
    buttons.style.gap = "10px";
    buttons.style.justifyContent = "flex-end";

    const okButton = document.createElement("button");
    okButton.type = "button";
    okButton.textContent = "OK";
    okButton.style.padding = "10px 18px";
    okButton.style.border = "none";
    okButton.style.borderRadius = "8px";
    okButton.style.background = "#1f4e79";
    okButton.style.color = "#fff";
    okButton.style.fontSize = "16px";
    okButton.style.cursor = "pointer";

    const cancelButton = document.createElement("button");
    cancelButton.type = "button";
    cancelButton.textContent = "Annuleren";
    cancelButton.style.padding = "10px 18px";
    cancelButton.style.border = "1px solid #d0d0d0";
    cancelButton.style.borderRadius = "8px";
    cancelButton.style.background = "#fff";
    cancelButton.style.color = "#111";
    cancelButton.style.fontSize = "16px";
    cancelButton.style.cursor = "pointer";

    let done = false;

    function cleanup(value) {
      if (done) return;
      done = true;

      try {
        document.body.removeChild(overlay);
      } catch {}

      resolve(value);
    }

    okButton.onclick = () => cleanup(input.value);
    cancelButton.onclick = () => cleanup(null);

    input.onkeydown = (ev) => {
      if (ev.key === "Enter") {
        ev.preventDefault();
        cleanup(input.value);
      }

      if (ev.key === "Escape") {
        ev.preventDefault();
        cleanup(null);
      }
    };

    overlay.onclick = (ev) => {
      if (ev.target === overlay) cleanup(null);
    };

    buttons.appendChild(cancelButton);
    buttons.appendChild(okButton);

    box.appendChild(title);
    box.appendChild(help);
    box.appendChild(input);
    box.appendChild(buttons);

    overlay.appendChild(box);
    document.body.appendChild(overlay);

    setTimeout(() => {
      try {
        input.focus();
        input.select();
      } catch {}
    }, 80);
  });
}

async function handleEmailGridClick(e, email) {
  stopGridClick(e);

  const mail = String(email || "").trim();

  if (!mail || mail === "-") return;

  const settings = await loadCommunicationSettingsSafe();

  if (settings.email_enabled === false) {
    alert("E-mail is niet actief in Communicatie-instellingen.");
    return;
  }

  window.location.href = `mailto:${encodeURIComponent(mail)}`;
}

function handlePhoneGridClick(e, phone) {
  stopGridClick(e);

  const tel = String(phone || "").trim();

  if (!tel || tel === "-") return;

  window.location.href = `tel:${tel}`;
}

async function handleMobileGridClick(e, mobile) {
  stopGridClick(e);

  const raw = String(mobile || "").trim();

  if (!raw || raw === "-") return;

  const settings = await loadCommunicationSettingsSafe();
  const normalized = normalizePhoneForCommunication(raw, settings.default_country_code || "+31");

  if (!normalized) return;

  const choice = await askCommunicationChoice(raw);
  const c = String(choice || "").trim().toLowerCase();

  if (!c) return;

  if (c === "1" || c === "bellen" || c === "bel") {
    window.location.href = `tel:${raw}`;
    return;
  }

  if (c === "2" || c === "sms") {
    if (settings.sms_enabled !== true) {
      alert("SMS is niet actief in Communicatie-instellingen.");
      return;
    }

    const text = window.prompt(
      `SMS naar ${raw}`,
      settings.default_sms_text || ""
    );

    if (text === null) return;

    const message = String(text || "").trim();

    if (!message) {
      alert("SMS tekst ontbreekt.");
      return;
    }

    try {
      await tryFetchJson(["/api/communications/sms"], {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to: raw,
          text: message,
        }),
      });

      alert(`SMS verstuurd naar ${raw}`);
    } catch (err) {
      alert(err?.message || "SMS verzenden mislukt.");
    }

    return;
  }

  if (c === "3" || c === "whatsapp" || c === "app") {
    if (settings.whatsapp_enabled === false) {
      alert("WhatsApp is niet actief in Communicatie-instellingen.");
      return;
    }

    const whatsappNumber = normalized.replace(/[^\d]/g, "");
    const text = String(settings.default_whatsapp_text || "").trim();
    const suffix = text ? `?text=${encodeURIComponent(text)}` : "";

    window.open(`https://wa.me/${whatsappNumber}${suffix}`, "_blank", "noopener,noreferrer");
  }
}

const emptyCriteria = {
  wildcard: "",
  relatievorm: "",
  companyCodeId: "",
  soortRelatie: "",
  administratie: "",
  regioBezoek: "",
  plaatsBezoek: "",
  regioCorrespondentie: "",
  plaatsCorrespondentie: "",
  opmerking: "",
  matchingGegevens: "",
  naam: "",
  voornaam: "",
  tussenvoegsel: "",
  achternaam: "",
  geslacht: "",
  actief: "j",
};

const emptyForm = {
  id: null,

  relation_entity_type: "company",

  first_name: "",
  initials: "",
  name_prefix: "",
  last_name: "",
  gender: "",
  birth_date: "",
  salutation: "",
  attention_to: "",

  name: "",
  search_name: "",
  company_code_id: "",
  company_code_name: "",
  parent_organisation_id: "",

  type: "",
  administration_type: "",
  first_contact_date: "",

  phone: "",
  mobile: "",
  email: "",
  website: "",

  visit_street: "",
  visit_house_number: "",
  visit_addition: "",
  visit_postcode: "",
  visit_city: "",
  visit_province: "",
  visit_region: "",
  visit_country: "",

  corr_street: "",
  corr_house_number: "",
  corr_addition: "",
  corr_postcode: "",
  corr_city: "",
  corr_province: "",
  corr_region: "",
  corr_country: "",

  invoice_digital: true,
  mailing_list: true,

  bank: "",
  iban: "",
  bic: "",
  account_name: "",
  g_iban: "",
  g_percentage: "",
  sales_factor: "",

  kvk: "",
  vat_number: "",
  vat_rate: "",

  payment_terms: "",
  payment_method: "",
  payment_term_days: "",
  credit_limit: "",

  remarks: "",

  active: true,
};

function payloadFromForm(form) {
  return {
    ...form,
    birth_date: form.birth_date || null,
    first_contact_date: form.first_contact_date || null,
    parent_organisation_id: form.parent_organisation_id ? Number(form.parent_organisation_id) : null,
    visit_house_number: toIntString(form.visit_house_number || ""),
    corr_house_number: toIntString(form.corr_house_number || ""),
    visit_postcode: String(form.visit_postcode || "").toUpperCase().replace(/\s+/g, ""),
    corr_postcode: String(form.corr_postcode || "").toUpperCase().replace(/\s+/g, ""),
    g_percentage: form.g_percentage === "" ? null : Number(toIntString(form.g_percentage)),
    sales_factor: form.sales_factor === "" ? null : parseDecimal(form.sales_factor),
    payment_term_days: Number(form.payment_term_days || 0),
    credit_limit: form.credit_limit === "" ? null : Number(toIntString(form.credit_limit)),
    invoice_digital: form.invoice_digital === true,
    mailing_list: form.mailing_list === true,
    active: form.active !== false,
    is_active: form.active !== false,
    actief: form.active !== false,
  };
}

export default function MatchingRelaties() {
  const rawUser =
    sessionStorage.getItem("user") ||
    localStorage.getItem("user") ||
    "";

  let role = "user";
  try {
    role = JSON.parse(rawUser || "{}")?.role || "user";
  } catch {}

  const isReadonlyRole = String(role || "").toLowerCase() === "readonly";

  const [items, setItems] = useState([]);
  const [criteria, setCriteria] = useState(emptyCriteria);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState("");
  const [speechInfo, setSpeechInfo] = useState("");

  const [sort, setSort] = useState({ key: "search", dir: "asc" });
  const [listIndex, setListIndex] = useState(-1);

  const [searchNameFilter, setSearchNameFilter] = useState("");
  const [entityFilter, setEntityFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [adminFilter, setAdminFilter] = useState("");
  const [pcFilter, setPcFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [regionFilter, setRegionFilter] = useState("");
  const [phoneFilter, setPhoneFilter] = useState("");
  const [mobileFilter, setMobileFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("j");
  const [companyCodeFilter, setCompanyCodeFilter] = useState("");

  const [companyCodeOptions, setCompanyCodeOptions] = useState([]);

  const [popupOpen, setPopupOpen] = useState(false);
  const [popupSaving, setPopupSaving] = useState(false);
  const [popupError, setPopupError] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [matchingRows, setMatchingRows] = useState([]);
  const [documentName, setDocumentName] = useState("");

  const searchRef = useRef(null);
  const rowRefs = useRef([]);
  const popupScrollRef = useRef(null);

  const wildcardRef = useRef(null);
  const relatievormRef = useRef(null);
  const companyCodeRef = useRef(null);
  const soortRelatieRef = useRef(null);
  const administratieRef = useRef(null);
  const regioBezoekRef = useRef(null);
  const plaatsBezoekRef = useRef(null);
  const regioCorrespondentieRef = useRef(null);
  const plaatsCorrespondentieRef = useRef(null);
  const opmerkingRef = useRef(null);
  const matchingRef = useRef(null);
  const naamRef = useRef(null);
  const voornaamRef = useRef(null);
  const tussenvoegselRef = useRef(null);
  const achternaamRef = useRef(null);
  const geslachtRef = useRef(null);
  const actiefRef = useRef(null);

  const criteriaRefs = useMemo(
    () => [
      wildcardRef,
      relatievormRef,
      companyCodeRef,
      soortRelatieRef,
      administratieRef,
      regioBezoekRef,
      plaatsBezoekRef,
      regioCorrespondentieRef,
      plaatsCorrespondentieRef,
      opmerkingRef,
      matchingRef,
      naamRef,
      voornaamRef,
      tussenvoegselRef,
      achternaamRef,
      geslachtRef,
      actiefRef,
    ],
    []
  );

  const criteriaRefMap = {
    wildcard: wildcardRef,
    relatievorm: relatievormRef,
    companyCodeId: companyCodeRef,
    soortRelatie: soortRelatieRef,
    administratie: administratieRef,
    regioBezoek: regioBezoekRef,
    plaatsBezoek: plaatsBezoekRef,
    regioCorrespondentie: regioCorrespondentieRef,
    plaatsCorrespondentie: plaatsCorrespondentieRef,
    opmerking: opmerkingRef,
    matchingGegevens: matchingRef,
    naam: naamRef,
    voornaam: voornaamRef,
    tussenvoegsel: tussenvoegselRef,
    achternaam: achternaamRef,
    geslacht: geslachtRef,
    actief: actiefRef,
  };

  useEffect(() => {
    loadCompanyCodes();
    loadItems();
  }, []);

  async function loadCompanyCodes() {
    try {
      const data = await tryFetchJson(["/api/company-codes"]);
      setCompanyCodeOptions(Array.isArray(data) ? data : []);
    } catch {
      setCompanyCodeOptions([]);
    }
  }

  async function loadItems() {
    try {
      setLoading(true);
      setError("");

      const data = await tryFetchJson([
        "/api/organisations",
      ]);

      const arr = Array.isArray(data) ? data : [];

      const enriched = await Promise.all(
        arr.map(async (it) => {
          try {
            const match = await tryFetchJson([
              `/api/organisations/${encodeURIComponent(it.id)}/matching`,
            ]);

            const matchingText = Array.isArray(match)
              ? match.map((m) => [
                  m.category,
                  m.name,
                  m.description,
                  m.level,
                  m.employee,
                ].filter(Boolean).join(" ")).join(" ")
              : "";

            return {
              ...it,
              matching_text: matchingText,
            };
          } catch {
            return {
              ...it,
              matching_text: "",
            };
          }
        })
      );

      setItems(enriched);
      setListIndex(-1);
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  }

  function setCrit(key, value) {
    setCriteria((prev) => ({ ...prev, [key]: value }));
  }

  function resetCriteria() {
    setCriteria(emptyCriteria);
    resetGridFilters();
    setListIndex(-1);
    setTimeout(() => searchRef.current?.focus?.(), 0);
  }

  function resetGridFilters() {
    setSearchNameFilter("");
    setEntityFilter("");
    setTypeFilter("");
    setAdminFilter("");
    setPcFilter("");
    setCityFilter("");
    setRegionFilter("");
    setPhoneFilter("");
    setMobileFilter("");
    setEmailFilter("");
    setActiveFilter("j");
    setCompanyCodeFilter("");
  }

  function searchableText(it) {
    return [
      it.id,
      relationTypeLabel(it),
      it.relation_entity_type,
      it.search_name,
      it.name,
      it.first_name,
      it.initials,
      it.name_prefix,
      it.last_name,
      it.gender,
      it.birth_date,
      it.salutation,
      it.attention_to,
      it.type,
      it.administration_type,
      it.first_contact_date,
      it.visit_street,
      it.visit_house_number,
      it.visit_postcode,
      it.visit_city,
      it.visit_region,
      it.visit_country,
      it.corr_street,
      it.corr_house_number,
      it.corr_postcode,
      it.corr_city,
      it.corr_region,
      it.corr_country,
      it.phone,
      it.mobile,
      it.email,
      it.website,
      it.kvk,
      it.vat_number,
      it.payment_terms,
      it.payment_method,
      companyCodeName(it),
      it.company_code,
      it.remarks,
      it.matching_text,
    ].join(" ");
  }

  const searchNameOptions = useMemo(() => uniqSorted(items.map((x) => displayName(x))), [items]);
  const entityOptions = useMemo(() => uniqSorted(items.map((x) => relationTypeShort(x))), [items]);
  const typeOptions = useMemo(() => uniqSorted(items.map((x) => x.type || "")), [items]);
  const adminOptions = useMemo(() => uniqSorted(items.map((x) => x.administration_type || "")), [items]);
  const pcOptions = useMemo(() => uniqSorted(items.map((x) => x.corr_postcode || x.visit_postcode || "")), [items]);
  const cityOptions = useMemo(() => uniqSorted(items.map((x) => x.corr_city || x.visit_city || "")), [items]);
  const regionOptions = useMemo(() => uniqSorted(items.map((x) => x.corr_region || x.visit_region || "")), [items]);
  const phoneOptions = useMemo(() => uniqSorted(items.map((x) => x.phone || "")), [items]);
  const mobileOptions = useMemo(() => uniqSorted(items.map((x) => x.mobile || "")), [items]);
  const emailOptions = useMemo(() => uniqSorted(items.map((x) => x.email || "")), [items]);
  const companyCodeGridOptions = useMemo(() => uniqSorted(items.map((x) => companyCodeName(x))), [items]);

  const filteredItems = useMemo(() => {
    const c = criteria;

    const match = (val, filt) => {
      if (!filt) return true;
      return String(val || "") === String(filt);
    };

    return (items || []).filter((it) => {
      if (!textIncludes(searchableText(it), c.wildcard)) return false;
      if (!textIncludes(relationTypeLabel(it), c.relatievorm)) return false;
      if (c.companyCodeId && companyCodeIdValue(it) !== String(c.companyCodeId)) return false;
      if (!textIncludes(it.type, c.soortRelatie)) return false;
      if (!textIncludes(it.administration_type, c.administratie)) return false;
      if (!textIncludes(it.visit_region, c.regioBezoek)) return false;
      if (!textIncludes(it.visit_city, c.plaatsBezoek)) return false;
      if (!textIncludes(it.corr_region, c.regioCorrespondentie)) return false;
      if (!textIncludes(it.corr_city, c.plaatsCorrespondentie)) return false;
      if (!textIncludes(it.remarks, c.opmerking)) return false;
      if (!textIncludes(it.matching_text, c.matchingGegevens)) return false;
      if (!textIncludes(it.name || it.search_name, c.naam)) return false;
      if (!textIncludes(it.first_name, c.voornaam)) return false;
      if (!textIncludes(it.name_prefix, c.tussenvoegsel)) return false;
      if (!textIncludes(it.last_name, c.achternaam)) return false;
      if (!textIncludes(it.gender, c.geslacht)) return false;
      if (!boolMatches(activeToBool(it), c.actief)) return false;

      if (!match(displayName(it), searchNameFilter)) return false;
      if (!match(relationTypeShort(it), entityFilter)) return false;
      if (!match(it.type || "", typeFilter)) return false;
      if (!match(it.administration_type || "", adminFilter)) return false;
      if (!match(it.corr_postcode || it.visit_postcode || "", pcFilter)) return false;
      if (!match(it.corr_city || it.visit_city || "", cityFilter)) return false;
      if (!match(it.corr_region || it.visit_region || "", regionFilter)) return false;
      if (!match(it.phone || "", phoneFilter)) return false;
      if (!match(it.mobile || "", mobileFilter)) return false;
      if (!match(it.email || "", emailFilter)) return false;
      if (!match(companyCodeName(it), companyCodeFilter)) return false;

      const active = activeToBool(it);
      if (activeFilter === "j" && active !== true) return false;
      if (activeFilter === "n" && active === true) return false;

      return true;
    });
  }, [
    items,
    criteria,
    searchNameFilter,
    entityFilter,
    typeFilter,
    adminFilter,
    pcFilter,
    cityFilter,
    regionFilter,
    phoneFilter,
    mobileFilter,
    emailFilter,
    activeFilter,
    companyCodeFilter,
  ]);

  const sortedItems = useMemo(() => {
    const copy = filteredItems.slice();
    const dirMul = sort.dir === "desc" ? -1 : 1;

    const cmp = (a, b) => {
      const aa = norm(a);
      const bb = norm(b);
      if (aa === bb) return 0;
      return aa < bb ? -1 : 1;
    };

    copy.sort((a, b) => {
      if (sort.key === "search") return dirMul * cmp(displayName(a), displayName(b));
      if (sort.key === "entity") return dirMul * cmp(relationTypeShort(a), relationTypeShort(b));
      if (sort.key === "type") return dirMul * cmp(a.type || "", b.type || "");
      if (sort.key === "admin") return dirMul * cmp(a.administration_type || "", b.administration_type || "");
      if (sort.key === "pc") return dirMul * cmp(a.corr_postcode || a.visit_postcode || "", b.corr_postcode || b.visit_postcode || "");
      if (sort.key === "city") return dirMul * cmp(a.corr_city || a.visit_city || "", b.corr_city || b.visit_city || "");
      if (sort.key === "region") return dirMul * cmp(a.corr_region || a.visit_region || "", b.corr_region || b.visit_region || "");
      if (sort.key === "phone") return dirMul * cmp(a.phone || "", b.phone || "");
      if (sort.key === "mobile") return dirMul * cmp(a.mobile || "", b.mobile || "");
      if (sort.key === "email") return dirMul * cmp(a.email || "", b.email || "");
      if (sort.key === "company") return dirMul * cmp(companyCodeName(a), companyCodeName(b));
      if (sort.key === "active") return dirMul * cmp(activeToBool(a) ? "1" : "0", activeToBool(b) ? "1" : "0");
      return dirMul * cmp(displayName(a), displayName(b));
    });

    return copy;
  }, [filteredItems, sort]);

  const gridCount = Math.min(sortedItems.length, 9999);

  function sortToggle(key) {
    setSort((s) => {
      if (s.key !== key) return { key, dir: "asc" };
      return { key, dir: s.dir === "asc" ? "desc" : "asc" };
    });
  }

  function sortArrow(key) {
    if (sort.key !== key) return "";
    return sort.dir === "asc" ? " ▲" : " ▼";
  }

  function filterSelect(value, setValue, options, includeYesNo = false) {
    return (
      <select value={value} onChange={(e) => setValue(e.target.value)} style={selectStyle}>
        <option value="">(alle)</option>
        {includeYesNo ? (
          <>
            <option value="j">j</option>
            <option value="n">n</option>
          </>
        ) : (
          options.map((x) => (
            <option key={x} value={x}>
              {x}
            </option>
          ))
        )}
      </select>
    );
  }

  async function openRelatie(row) {
    if (!row) return;

    try {
      setPopupError("");

      let detail = row;
      try {
        const data = await tryFetchJson([
          `/api/organisations/${encodeURIComponent(row.id)}`,
        ]);
        if (data && typeof data === "object") {
          detail = data;
        }
      } catch {}

      setForm({
        ...emptyForm,
        ...detail,
        birth_date: isoDate(detail.birth_date),
        first_contact_date: isoDate(detail.first_contact_date),
        active: activeToBool(detail),
      });

      const match = await tryFetchJson([
        `/api/organisations/${encodeURIComponent(row.id)}/matching`,
      ]);

      setMatchingRows(Array.isArray(match) ? match : []);
      setDocumentName(`Matching ${displayName(detail || row)}`.trim());
      setPopupOpen(true);

      setTimeout(() => {
        try {
          popupScrollRef.current?.scrollTo?.({ top: 0 });
        } catch {}
      }, 0);
    } catch (err) {
      setPopupError(err?.message || String(err));
    }
  }

  async function saveRelatie() {
    if (isReadonlyRole || !form.id) return;

    try {
      setPopupSaving(true);
      setPopupError("");

      const saved = await tryFetchJson([`/api/organisations/${form.id}`], {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payloadFromForm(form)),
      });

      if (saved && typeof saved === "object") {
        setItems((prev) =>
          prev.map((x) =>
            String(x.id) === String(saved.id)
              ? { ...x, ...saved, matching_text: x.matching_text || "" }
              : x
          )
        );

        setForm((prev) => ({
          ...prev,
          ...saved,
          birth_date: isoDate(saved.birth_date),
          first_contact_date: isoDate(saved.first_contact_date),
          active: activeToBool(saved),
        }));
      }

      setFlash("Relatie opgeslagen ✅");
      setTimeout(() => setFlash(""), 1400);
    } catch (err) {
      setPopupError(err?.message || String(err));
    } finally {
      setPopupSaving(false);
    }
  }

  function buildPrintableHtml(printMode = "internal") {
    const isExternal = printMode === "external";

    const bezoekadres = [
      form.visit_street,
      form.visit_house_number,
      form.visit_addition,
      form.visit_postcode,
      form.visit_city,
      form.visit_region,
      form.visit_country,
    ].filter(Boolean).join(" ");

    const corradres = [
      form.corr_street,
      form.corr_house_number,
      form.corr_addition,
      form.corr_postcode,
      form.corr_city,
      form.corr_region,
      form.corr_country,
    ].filter(Boolean).join(" ");

    const allRows = [
      ["Zoeknaam", displayName(form), "internal"],
      ["Relatievorm", relationTypeLabel(form), "both"],
      ["Naam", form.name, "internal"],
      ["Voornaam", form.first_name, "internal"],
      ["Voorletters", form.initials, "internal"],
      ["Tussenvoegsel", form.name_prefix, "internal"],
      ["Achternaam", form.last_name, "internal"],
      ["Geslacht", form.gender, "both"],
      ["Geboortedatum", formatDateNL(form.birth_date), "internal"],
      ["Soort relatie", form.type, "both"],
      ["Administratie", form.administration_type, "both"],
      ["Datum 1e contact", formatDateNL(form.first_contact_date), "internal"],
      ["Telefoon", form.phone, "internal"],
      ["Mobiel", form.mobile, "internal"],
      ["E-mail", form.email, "internal"],
      ["Website", form.website, "both"],
      ["Bezoekadres", bezoekadres, "internal"],
      ["Correspondentieadres", corradres, "internal"],
      ["KvK", form.kvk, "internal"],
      ["BTW nummer", form.vat_number, "internal"],
      ["Betaaltermijn", form.payment_terms, "internal"],
      ["Betaalmethode", form.payment_method, "internal"],
      ["Actief", form.active !== false ? "Ja" : "Nee", "both"],
      ["Opmerking", form.remarks, "internal"],
    ];

    const rows = isExternal
      ? allRows.filter(([, , visibility]) => visibility === "both")
      : allRows;

    const tr = rows.map(([k, v]) => `
      <tr>
        <td style="width:190px;font-weight:bold;vertical-align:top;border-bottom:1px solid #ddd;padding:6px;">${escapeHtml(k)}</td>
        <td style="border-bottom:1px solid #ddd;padding:6px;white-space:pre-wrap;">${escapeHtml(v || "")}</td>
      </tr>
    `).join("");

    const matching = (matchingRows || []).map((m) => `
      <tr>
        <td style="border-bottom:1px solid #ddd;padding:6px;">${escapeHtml(m.category || "")}</td>
        <td style="border-bottom:1px solid #ddd;padding:6px;">${escapeHtml(m.name || "")}</td>
        <td style="border-bottom:1px solid #ddd;padding:6px;">${escapeHtml(m.level || "")}</td>
        <td style="border-bottom:1px solid #ddd;padding:6px;">${escapeHtml(m.description || "")}</td>
        <td style="border-bottom:1px solid #ddd;padding:6px;">${m.diploma ? "Ja" : "Nee"}</td>
        <td style="border-bottom:1px solid #ddd;padding:6px;">${isExternal ? "" : escapeHtml(m.employee || "")}</td>
      </tr>
    `).join("");

    const titleSuffix = isExternal ? "extern" : "intern";

    return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(documentName || "Matching Relaties")} - ${titleSuffix}</title>
</head>
<body style="font-family:Arial,sans-serif;font-size:13px;color:#111;">
  <div style="margin-bottom:18px;">
    <h1 style="margin-bottom:4px;">Matching Relaties</h1>
    <div style="margin-bottom:18px;color:#555;">${escapeHtml(documentName || "")} ${isExternal ? "- extern" : "- intern"}</div>
    ${isExternal ? `<div style="padding:8px;border:1px solid #ddd;background:#fafafa;">Externe versie: zonder naam, adres en vertrouwelijke gegevens.</div>` : ""}
  </div>

  <h2>Relatiekaart</h2>
  <table style="width:100%;border-collapse:collapse;">${tr}</table>

  <h2 style="margin-top:24px;">Matching gegevens</h2>
  <table style="width:100%;border-collapse:collapse;">
    <thead>
      <tr>
        <th style="text-align:left;border-bottom:2px solid #333;padding:6px;">Rubriek</th>
        <th style="text-align:left;border-bottom:2px solid #333;padding:6px;">Naam</th>
        <th style="text-align:left;border-bottom:2px solid #333;padding:6px;">Niveau</th>
        <th style="text-align:left;border-bottom:2px solid #333;padding:6px;">Omschrijving</th>
        <th style="text-align:left;border-bottom:2px solid #333;padding:6px;">Diploma</th>
        <th style="text-align:left;border-bottom:2px solid #333;padding:6px;">Werknemer</th>
      </tr>
    </thead>
    <tbody>${matching || `<tr><td colspan="6" style="padding:8px;">Geen matching gegevens.</td></tr>`}</tbody>
  </table>
</body>
</html>`;
  }

  function printPopup(printMode = "internal") {
    const html = buildPrintableHtml(printMode);
    const w = window.open("", "_blank", "width=900,height=700");
    if (!w) {
      window.alert("Popup kon niet worden geopend. Controleer popup-blokkering.");
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 250);
  }

  async function saveAsDocument() {
    if (!form.id) return;

    const name = safeTrim(documentName);

    if (!name) {
      setPopupError("Vul eerst een matchingnaam in.");
      return;
    }

    try {
      setPopupSaving(true);
      setPopupError("");

      const data = await tryFetchJson(
        [`/api/organisations/${form.id}/matching-document`],
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            matching_name: name,
            html: buildPrintableHtml("internal"),
            document_type: "Rel_krt",
          }),
        }
      );

      setFlash(`Document opgeslagen ✅ ${data?.file_name || ""}`);
      setTimeout(() => setFlash(""), 1800);
    } catch (err) {
      setPopupError(err?.message || String(err));
    } finally {
      setPopupSaving(false);
    }
  }

  function moveCursor(delta) {
    setListIndex((prev) => {
      const L = sortedItems.length;
      if (L <= 0) return -1;
      const start = prev < 0 ? 0 : prev;
      const next = Math.max(0, Math.min(L - 1, start + delta));
      setTimeout(() => rowRefs.current[next]?.scrollIntoView?.({ block: "nearest" }), 0);
      return next;
    });
  }

  function openCurrentRow() {
    const idx = listIndex < 0 ? 0 : listIndex;
    const row = sortedItems[idx];
    if (row) openRelatie(row);
  }

  function focusNextCriteria(dir = +1) {
    const els = criteriaRefs.map((r) => r.current).filter(Boolean);
    if (!els.length) return;
    const active = document.activeElement;
    let idx = els.findIndex((el) => el === active);
    if (idx === -1) idx = dir > 0 ? -1 : els.length;
    let next = idx + dir;
    if (next < 0) next = 0;
    if (next > els.length - 1) next = els.length - 1;
    els[next]?.focus?.();
    els[next]?.scrollIntoView?.({ block: "nearest" });
  }

  const voice = useVoiceCommands({
    lang: "nl-NL",
    timeoutMs: 6000,
    onCommand: (cmd) => {
      if (!cmd?.type) return false;
      if (cmd.type === "next") return focusNextCriteria(+1), true;
      if (cmd.type === "prev") return focusNextCriteria(-1), true;
      if (cmd.type === "refresh") return resetCriteria(), true;
      if (cmd.type === "select") return openCurrentRow(), true;
      if (cmd.type === "save") return saveRelatie(), true;
      return false;
    },
    onDictation: (text) => {
      const raw = String(text || "").trim();
      if (!raw) return;

      setSpeechInfo(`🎙 ${raw}`);
      setTimeout(() => setSpeechInfo(""), 2000);

      const t = norm(raw);
      if (t === "volgende") return focusNextCriteria(+1);
      if (t === "vorige" || t === "terug") return focusNextCriteria(-1);
      if (t === "vernieuw" || t === "vernieuwen") return resetCriteria();
      if (t === "selecteer" || t === "item" || t === "open") return openCurrentRow();
      if (t === "print" || t === "printen") return popupOpen ? printPopup("internal") : undefined;
      if (t === "opslaan") return popupOpen ? saveRelatie() : undefined;
      if (t === "sluiten" || t === "klaar" || t === "stop") return popupOpen ? setPopupOpen(false) : undefined;
      if (t === "leeg") return resetCriteria();

      const active = document.activeElement;
      const entry = Object.entries(criteriaRefMap).find(([, ref]) => ref.current === active);

      if (entry) {
        const [key] = entry;
        setCrit(key, isEmptyWord(raw) ? "" : raw);
        return;
      }

      setCrit("wildcard", isEmptyWord(raw) ? "" : raw);
    },
  });

  const isMobile = window.innerWidth <= 768;
  const RIGHT_WIDTH = isMobile ? "100%" : 320;
  const TABLE_MIN_WIDTH = 1280;

  const colWidths = {
    search: 150,
    entity: 55,
    type: 105,
    admin: 105,
    pc: 75,
    city: 105,
    region: 90,
    phone: 90,
    mobile: 90,
    email: 130,
    active: 55,
    company: 105,
  };

  const pageGridStyle = {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : `minmax(0, 1fr) ${RIGHT_WIDTH}px`,
    gap: isMobile ? 18 : 14,
    alignItems: "start",
    minWidth: 0,
  };

  const searchInputStyle = {
    padding: "7px 10px",
    width: isMobile ? "100%" : 220,
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    outline: "none",
    boxSizing: "border-box",
  };

  const inputStyle = {
    display: "block",
    padding: "7px 10px",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    boxSizing: "border-box",
    marginBottom: 7,
  };

  const selectStyle = {
    display: "block",
    height: 30,
    padding: "0 8px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
  };

  const buttonStyle = {
    padding: "7px 12px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    color: "#222",
    cursor: "pointer",
    whiteSpace: "nowrap",
    fontSize: 13,
    fontFamily: "inherit",
  };

  const buttonDisabledStyle = {
    ...buttonStyle,
    opacity: 0.55,
    cursor: "not-allowed",
  };

  const buttonPrimary = (bg) => ({
    ...buttonStyle,
    background: bg,
    color: "white",
    border: "1px solid rgba(0,0,0,0.05)",
  });

  const popupPrintButtonStyle = {
    ...buttonStyle,
    background: "#6c757d",
    color: "white",
    border: "1px solid rgba(0,0,0,0.05)",
  };

  const popupDocumentButtonStyle = {
    ...buttonStyle,
    background: "#198754",
    color: "white",
    border: "1px solid rgba(0,0,0,0.05)",
  };

  const popupCloseButtonStyle = {
    ...buttonStyle,
    background: "#0d6efd",
    color: "white",
    border: "1px solid rgba(0,0,0,0.05)",
  };

  const thBase = {
    textAlign: "left",
    fontSize: 12,
    color: "#444",
    fontWeight: 700,
    padding: "8px 6px",
    borderBottom: "1px solid #e6e6e6",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 5,
    background: "#fff",
    userSelect: "none",
  };

  const thClickable = { ...thBase, cursor: "pointer" };

  const filterThStyle = {
    ...thBase,
    fontWeight: 400,
    padding: "6px 6px 8px 6px",
    borderBottom: "1px solid #ededed",
    top: 32,
    zIndex: 4,
    cursor: "default",
  };

  const tdStyle = {
    padding: "7px 6px",
    borderBottom: "1px solid #efefef",
    fontSize: 13,
    color: "#222",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const rowBgCursor = "#f3f7ff";
  const rowBgSelected = "#e6f0ff";
  const labelStyle = { fontSize: 13, color: "#333", marginBottom: 2, display: "block" };

  function renderCriteriaField(label, key, ref = null, type = "text") {
    return (
      <>
        <label style={labelStyle}>{label}</label>
        <input
          ref={ref}
          type={type}
          value={criteria[key] || ""}
          onChange={(e) => setCrit(key, e.target.value)}
          style={inputStyle}
        />
      </>
    );
  }

  function renderPopupText(label, key, type = "text") {
    return (
      <>
        <label style={labelStyle}>{label}</label>
        <input
          type={type}
          value={form[key] || ""}
          disabled={isReadonlyRole}
          onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))}
          style={inputStyle}
        />
      </>
    );
  }

  function renderPopupCommunicationText(label, key, type = "text", communicationType = "") {
    const value = String(form[key] || "").trim();

    function handleCommunicationClick(e) {
      if (!value) return;

      if (communicationType === "phone") {
        handlePhoneGridClick(e, value);
        return;
      }

      if (communicationType === "mobile") {
        handleMobileGridClick(e, value);
        return;
      }

      if (communicationType === "email") {
        handleEmailGridClick(e, value);
      }
    }

    return (
      <>
        <label style={labelStyle}>{label}</label>

        <div style={{ display: "grid", gridTemplateColumns: value ? "1fr auto" : "1fr", gap: 6 }}>
          <input
            type={type}
            value={form[key] || ""}
            disabled={isReadonlyRole}
            onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))}
            style={inputStyle}
          />

          {value && (
            <button
              type="button"
              onClick={handleCommunicationClick}
              style={{
                ...buttonStyle,
                padding: "7px 9px",
                whiteSpace: "nowrap",
              }}
              title={
                communicationType === "email"
                  ? `E-mailen: ${value}`
                  : communicationType === "mobile"
                    ? `Bellen / SMS / WhatsApp: ${value}`
                    : `Bellen: ${value}`
              }
            >
              {communicationType === "email" ? "✉️" : communicationType === "mobile" ? "📱" : "☎️"}
            </button>
          )}
        </div>
      </>
    );
  }

  function renderPopupCheck(label, key) {
    return (
      <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, marginBottom: 8 }}>
        <input
          type="checkbox"
          checked={form[key] === true}
          disabled={isReadonlyRole}
          onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.checked }))}
        />
        {label}
      </label>
    );
  }

  function renderPopupSelect(label, key, options) {
    return (
      <>
        <label style={labelStyle}>{label}</label>
        <select
          value={form[key] || ""}
          disabled={isReadonlyRole}
          onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))}
          style={inputStyle}
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </>
    );
  }

  return (
    <div>
      <div style={pageGridStyle}>
        <div style={{ minWidth: 0 }}>
          <h1 style={{ marginTop: 0, marginBottom: 10 }}>
            Matching Relaties <span style={{ fontSize: 12, opacity: 0.5 }}>{VERSION}</span>
          </h1>

          {!isMobile ? (
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "nowrap" }}>
              <input
                ref={searchRef}
                value={criteria.wildcard}
                onChange={(e) => setCrit("wildcard", e.target.value)}
                placeholder="Wildcard zoeken..."
                style={{ ...inputStyle, maxWidth: 420 }}
              />

              <div
                title={`Aantal regels in het grid: ${gridCount}`}
                style={{
                  minWidth: 52,
                  height: 32,
                  padding: "0 10px",
                  border: "1px solid #d9d9d9",
                  borderRadius: 4,
                  background: "#f7fafc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#1f4e79",
                  boxSizing: "border-box",
                  whiteSpace: "nowrap",
                }}
              >
                {gridCount}
              </div>

              <button
                onMouseDown={(e) => e.preventDefault()}
                onTouchStart={(e) => e.preventDefault()}
                onClick={() => voice.startOnce?.()}
                disabled={!voice.supported || voice.listening}
                style={!voice.supported || voice.listening ? buttonDisabledStyle : buttonStyle}
              >
                {voice.listening ? "🎤 Luistert..." : "🎤 Spreek"}
              </button>

              <button onClick={resetCriteria} style={buttonStyle}>
                🔄 Vernieuw
              </button>
            </div>
          ) : (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) 58px",
                  gap: 6,
                  marginBottom: 8,
                  alignItems: "center",
                }}
              >
                <input
                  ref={searchRef}
                  value={criteria.wildcard}
                  onChange={(e) => setCrit("wildcard", e.target.value)}
                  placeholder="Wildcard zoeken..."
                  style={{
                    ...inputStyle,
                    width: "100%",
                    minWidth: 0,
                  }}
                />

                <div
                  title={`Aantal regels in het grid: ${gridCount}`}
                  style={{
                    height: 34,
                    minHeight: 34,
                    padding: "0 8px",
                    border: "1px solid #d9d9d9",
                    borderRadius: 4,
                    background: "#f7fafc",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#1f4e79",
                    boxSizing: "border-box",
                    whiteSpace: "nowrap",
                    lineHeight: "1",
                  }}
                >
                  {gridCount}
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginTop: 8 }}>
                <button
                  onClick={() => voice.startOnce?.()}
                  disabled={!voice.supported || voice.listening}
                  style={!voice.supported || voice.listening ? buttonDisabledStyle : buttonStyle}
                >
                  {voice.listening ? "🎤..." : "🎤 Spreek"}
                </button>

                <button onClick={resetCriteria} style={buttonStyle}>
                  🔄 Vernieuw
                </button>
              </div>
            </>
          )}

          <div style={{ marginTop: 10 }}>
            {loading && <div>Laden...</div>}
            {error && <div style={{ color: "crimson" }}>{error}</div>}
            {popupError && !popupOpen && <div style={{ color: "crimson" }}>{popupError}</div>}
            {flash && <div style={{ marginBottom: 8, color: "green" }}>{flash}</div>}
            {speechInfo && <div style={{ opacity: 0.75 }}>{speechInfo}</div>}
            {voice.error && voice.error !== "no-speech" && (
              <div style={{ color: "crimson" }}>Spraakfout: {voice.error}</div>
            )}
          </div>

          {!loading && !error && (
            <div style={{ marginTop: 8, minWidth: 0 }}>
              <div
                style={{
                  overflowX: "scroll",
                  overflowY: "auto",
                  maxHeight: isMobile ? "calc(100vh - 245px)" : "calc(100vh - 150px)",
                  border: "1px solid #f0f0f0",
                  borderRadius: 6,
                  background: "#fff",
                }}
              >
                <div style={{ minWidth: TABLE_MIN_WIDTH }}>
                  <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, tableLayout: "fixed" }}>
                    <colgroup>
                      <col style={{ width: colWidths.search }} />
                      <col style={{ width: colWidths.entity }} />
                      <col style={{ width: colWidths.type }} />
                      <col style={{ width: colWidths.admin }} />
                      <col style={{ width: colWidths.pc }} />
                      <col style={{ width: colWidths.city }} />
                      <col style={{ width: colWidths.region }} />
                      <col style={{ width: colWidths.phone }} />
                      <col style={{ width: colWidths.mobile }} />
                      <col style={{ width: colWidths.email }} />
                      <col style={{ width: colWidths.active }} />
                      <col style={{ width: colWidths.company }} />
                    </colgroup>

                    <thead>
                      <tr>
                        <th style={thClickable} onClick={() => sortToggle("search")}>Zoeknaam{sortArrow("search")}</th>
                        <th style={thClickable} onClick={() => sortToggle("entity")}>O/P{sortArrow("entity")}</th>
                        <th style={thClickable} onClick={() => sortToggle("type")}>Soort{sortArrow("type")}</th>
                        <th style={thClickable} onClick={() => sortToggle("admin")}>Administratie{sortArrow("admin")}</th>
                        <th style={thClickable} onClick={() => sortToggle("pc")}>Postcode{sortArrow("pc")}</th>
                        <th style={thClickable} onClick={() => sortToggle("city")}>Plaats{sortArrow("city")}</th>
                        <th style={thClickable} onClick={() => sortToggle("region")}>Regio{sortArrow("region")}</th>
                        <th style={thClickable} onClick={() => sortToggle("phone")}>Telefoon{sortArrow("phone")}</th>
                        <th style={thClickable} onClick={() => sortToggle("mobile")}>Mobiel{sortArrow("mobile")}</th>
                        <th style={thClickable} onClick={() => sortToggle("email")}>E-mail{sortArrow("email")}</th>
                        <th style={thClickable} onClick={() => sortToggle("active")}>Actief{sortArrow("active")}</th>
                        <th style={thClickable} onClick={() => sortToggle("company")}>Bedrijfscode{sortArrow("company")}</th>
                      </tr>

                      <tr>
                        <th style={filterThStyle}>{filterSelect(searchNameFilter, setSearchNameFilter, searchNameOptions)}</th>
                        <th style={filterThStyle}>{filterSelect(entityFilter, setEntityFilter, entityOptions)}</th>
                        <th style={filterThStyle}>{filterSelect(typeFilter, setTypeFilter, typeOptions)}</th>
                        <th style={filterThStyle}>{filterSelect(adminFilter, setAdminFilter, adminOptions)}</th>
                        <th style={filterThStyle}>{filterSelect(pcFilter, setPcFilter, pcOptions)}</th>
                        <th style={filterThStyle}>{filterSelect(cityFilter, setCityFilter, cityOptions)}</th>
                        <th style={filterThStyle}>{filterSelect(regionFilter, setRegionFilter, regionOptions)}</th>
                        <th style={filterThStyle}>{filterSelect(phoneFilter, setPhoneFilter, phoneOptions)}</th>
                        <th style={filterThStyle}>{filterSelect(mobileFilter, setMobileFilter, mobileOptions)}</th>
                        <th style={filterThStyle}>{filterSelect(emailFilter, setEmailFilter, emailOptions)}</th>
                        <th style={filterThStyle}>{filterSelect(activeFilter, setActiveFilter, [], true)}</th>
                        <th style={filterThStyle}>{filterSelect(companyCodeFilter, setCompanyCodeFilter, companyCodeGridOptions)}</th>
                      </tr>
                    </thead>

                    <tbody>
                      {sortedItems.map((it, idx) => {
                        const isCursor = idx === listIndex;
                        return (
                          <tr
                            key={it.id}
                            ref={(el) => (rowRefs.current[idx] = el)}
                            onClick={() => {
                              setListIndex(idx);
                              openRelatie(it);
                            }}
                            style={{
                              background: isCursor ? rowBgCursor : "white",
                              cursor: "pointer",
                            }}
                          >
                            <td style={{ ...tdStyle, background: isCursor ? rowBgSelected : undefined }} title={displayName(it)}>{displayName(it) || "-"}</td>
                            <td style={tdStyle}>{relationTypeShort(it)}</td>
                            <td style={tdStyle}>{it.type || "-"}</td>
                            <td style={tdStyle}>{it.administration_type || "-"}</td>
                            <td style={tdStyle}>{it.corr_postcode || it.visit_postcode || "-"}</td>
                            <td style={tdStyle}>{it.corr_city || it.visit_city || "-"}</td>
                            <td style={tdStyle}>{it.corr_region || it.visit_region || "-"}</td>
                            <td style={tdStyle}>
                              {it.phone ? (
                                <button onClick={(e) => handlePhoneGridClick(e, it.phone)} style={{ border: "none", background: "transparent", padding: 0, cursor: "pointer", color: "#1f4e79" }}>
                                  {it.phone}
                                </button>
                              ) : "-"}
                            </td>
                            <td style={tdStyle}>
                              {it.mobile ? (
                                <button onClick={(e) => handleMobileGridClick(e, it.mobile)} style={{ border: "none", background: "transparent", padding: 0, cursor: "pointer", color: "#1f4e79" }}>
                                  {it.mobile}
                                </button>
                              ) : "-"}
                            </td>
                            <td style={tdStyle}>
                              {it.email ? (
                                <button onClick={(e) => handleEmailGridClick(e, it.email)} style={{ border: "none", background: "transparent", padding: 0, cursor: "pointer", color: "#1f4e79" }}>
                                  {it.email}
                                </button>
                              ) : "-"}
                            </td>
                            <td style={tdStyle}>{activeToBool(it) ? "j" : "n"}</td>
                            <td style={tdStyle} title={companyCodeName(it)}>{companyCodeName(it) || "-"}</td>
                          </tr>
                        );
                      })}

                      {sortedItems.length === 0 && (
                        <tr>
                          <td colSpan={12} style={{ padding: 12, opacity: 0.7 }}>
                            Geen resultaten.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        <div
          style={{
            width: isMobile ? "100%" : RIGHT_WIDTH,
            minWidth: isMobile ? 0 : RIGHT_WIDTH,
            maxWidth: isMobile ? "100%" : RIGHT_WIDTH,
            position: isMobile ? "relative" : "sticky",
            top: isMobile ? "auto" : 0,
            alignSelf: "start",
            maxHeight: isMobile ? "none" : "calc(100vh - 40px)",
            overflowY: isMobile ? "visible" : "auto",
            background: "linear-gradient(180deg, #0b4f7a 0%, #083b5c 100%)",
            borderRadius: 10,
            padding: 10,
            boxSizing: "border-box",
            marginTop: isMobile ? 6 : 0,
            paddingTop: isMobile ? 6 : 10,
          }}
        >
          <div style={{ background: "white", borderRadius: 10, padding: 12, boxSizing: "border-box", width: "100%", maxWidth: "100%", minWidth: 0, overflow: "hidden" }}>
            <h3 style={{ marginTop: 0, marginBottom: 8 }}>Selectiecriteria</h3>

            {renderCriteriaField("Wildcard", "wildcard", wildcardRef)}

            <label style={labelStyle}>Relatievorm</label>
            <select ref={relatievormRef} value={criteria.relatievorm} onChange={(e) => setCrit("relatievorm", e.target.value)} style={inputStyle}>
              <option value="">Alle</option>
              <option value="Organisatie">Organisatie</option>
              <option value="Particulier">Particulier</option>
            </select>

            <label style={labelStyle}>Bedrijfscode</label>
            <select
              ref={companyCodeRef}
              value={criteria.companyCodeId || ""}
              onChange={(e) => setCrit("companyCodeId", e.target.value)}
              style={inputStyle}
            >
              <option value="">Alle</option>
              {companyCodeOptions.map((cc) => (
                <option key={cc.id} value={String(cc.id)}>
                  {cc.display_name || cc.name || cc.code || cc.id}
                </option>
              ))}
            </select>

            {renderCriteriaField("Soort relatie", "soortRelatie", soortRelatieRef)}
            {renderCriteriaField("Administratie", "administratie", administratieRef)}
            {renderCriteriaField("Regio bezoek", "regioBezoek", regioBezoekRef)}
            {renderCriteriaField("Plaats bezoek", "plaatsBezoek", plaatsBezoekRef)}
            {renderCriteriaField("Regio correspondentie", "regioCorrespondentie", regioCorrespondentieRef)}
            {renderCriteriaField("Plaats correspondentie", "plaatsCorrespondentie", plaatsCorrespondentieRef)}
            {renderCriteriaField("Opmerking", "opmerking", opmerkingRef)}
            {renderCriteriaField("Matching gegevens", "matchingGegevens", matchingRef)}
            {renderCriteriaField("Naam / organisatie", "naam", naamRef)}
           {renderCriteriaField("Voornaam", "voornaam", voornaamRef)}
            {renderCriteriaField("Tussenvoegsel", "tussenvoegsel", tussenvoegselRef)}
            {renderCriteriaField("Achternaam", "achternaam", achternaamRef)}

            <label style={labelStyle}>Geslacht</label>
            <select
              ref={geslachtRef}
              value={criteria.geslacht}
              onChange={(e) => setCrit("geslacht", e.target.value)}
              style={inputStyle}
            >
              <option value="">(geen)</option>
              <option value="m">m</option>
              <option value="v">v</option>
              <option value="o">o</option>
            </select>

            <label style={labelStyle}>Actief j/n</label>
            <select ref={actiefRef} value={criteria.actief} onChange={(e) => setCrit("actief", e.target.value)} style={inputStyle}>
              <option value="">Alle</option>
              <option value="j">Ja</option>
              <option value="n">Nee</option>
            </select>
          </div>
        </div>
      </div>

      {popupOpen && (
        <div
          onClick={() => setPopupOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: isMobile ? 8 : 18,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(1180px, 100%)",
              maxHeight: "92vh",
              overflow: "hidden",
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                padding: isMobile ? 10 : 14,
                borderBottom: "1px solid #e6e6e6",
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr auto",
                gap: 10,
                alignItems: "center",
              }}
            >
              <div>
                <h2 style={{ margin: 0 }}>
                  Relatiekaart <span style={{ fontSize: 12, opacity: 0.55 }}>{VERSION}</span>
                </h2>
                <div style={{ opacity: 0.7, fontSize: 13 }}>{displayName(form) || "Relatie"}</div>
              </div>

              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: isMobile ? "stretch" : "flex-end" }}>
                <button onClick={() => printPopup("internal")} style={popupPrintButtonStyle}>
                  🖨 Printen intern
                </button>

                <button onClick={() => printPopup("external")} style={popupPrintButtonStyle}>
                  🖨 Printen extern
                </button>

                <button
                  onClick={saveAsDocument}
                  disabled={popupSaving}
                  style={popupSaving ? buttonDisabledStyle : popupDocumentButtonStyle}
                >
                  🗂 Opslaan als document
                </button>

                <button onClick={() => setPopupOpen(false)} style={popupCloseButtonStyle}>
                  Sluiten
                </button>
              </div>
            </div>

            <div ref={popupScrollRef} style={{ overflow: "auto", padding: isMobile ? 10 : 14 }}>
              {popupError && <div style={{ color: "crimson", marginBottom: 8 }}>{popupError}</div>}
              {flash && <div style={{ color: "#0b7a3b", marginBottom: 8 }}>{flash}</div>}

              <div style={{ marginBottom: 10 }}>
                <label style={labelStyle}>Matchingnaam / printkop</label>
                <input
                  value={documentName}
                  onChange={(e) => setDocumentName(e.target.value)}
                  style={inputStyle}
                />
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
                  gap: 12,
                  alignItems: "start",
                }}
              >
                <div>
                  <h3>Algemeen</h3>

                  {renderPopupSelect("Relatievorm", "relation_entity_type", [
                    { value: "company", label: "Organisatie" },
                    { value: "private", label: "Particulier" },
                  ])}

                  {String(form.relation_entity_type || "company").toLowerCase() === "private" ? (
                    <>
                      {renderPopupText("Voornaam", "first_name")}
                      {renderPopupText("Voorletters", "initials")}
                      {renderPopupText("Tussenvoegsel", "name_prefix")}
                      {renderPopupText("Achternaam", "last_name")}

                      {renderPopupSelect("Geslacht", "gender", [
                        { value: "", label: "(geen)" },
                        { value: "m", label: "m" },
                        { value: "v", label: "v" },
                        { value: "o", label: "o" },
                      ])}

                      {renderPopupText("Geboortedatum", "birth_date", "date")}
                      {renderPopupText("Aanhef", "salutation")}
                      {renderPopupText("T.a.v.", "attention_to")}
                    </>
                  ) : (
                    <>
                      {renderPopupText("Organisatienaam", "name")}
                      {renderPopupText("T.a.v.", "attention_to")}
                    </>
                  )}

                  {renderPopupText("Zoeknaam", "search_name")}
                  {renderPopupCheck("Actief", "active")}
                </div>

                <div>
                  <h3>CRM</h3>
                  {renderPopupText("Soort relatie", "type")}
                  {renderPopupText("Administratie", "administration_type")}
                  {renderPopupText("Datum 1e contact", "first_contact_date", "date")}
                  {renderPopupCommunicationText("Telefoon", "phone", "text", "phone")}
                  {renderPopupCommunicationText("Mobiel", "mobile", "text", "mobile")}
                  {renderPopupCommunicationText("E-mail", "email", "email", "email")}
                  {renderPopupText("Website", "website")}
                  {renderPopupText("KvK", "kvk")}
                  {renderPopupText("BTW nummer", "vat_number")}
                  {renderPopupText("BTW tarief", "vat_rate")}
                  {renderPopupText("Betaaltermijn", "payment_terms")}
                </div>

                <div>
                  <h3>Bezoekadres</h3>
                  {renderPopupText("Straat", "visit_street")}
                  {renderPopupText("Huisnummer", "visit_house_number")}
                  {renderPopupText("Toevoeging", "visit_addition")}
                  {renderPopupText("Postcode", "visit_postcode")}
                  {renderPopupText("Plaats", "visit_city")}
                  {renderPopupText("Provincie", "visit_province")}
                  {renderPopupText("Regio", "visit_region")}
                  {renderPopupText("Land", "visit_country")}
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
                  gap: 12,
                  marginTop: 12,
                  alignItems: "start",
                }}
              >
                <div>
                  <h3>Correspondentieadres</h3>
                  {renderPopupText("Straat", "corr_street")}
                  {renderPopupText("Huisnummer", "corr_house_number")}
                  {renderPopupText("Toevoeging", "corr_addition")}
                  {renderPopupText("Postcode", "corr_postcode")}
                  {renderPopupText("Plaats", "corr_city")}
                  {renderPopupText("Provincie", "corr_province")}
                  {renderPopupText("Regio", "corr_region")}
                  {renderPopupText("Land", "corr_country")}
                </div>

                <div>
                  <h3>Bank / betaling</h3>
                  {renderPopupText("Bank", "bank")}
                  {renderPopupText("IBAN", "iban")}
                  {renderPopupText("BIC", "bic")}
                  {renderPopupText("Rekeningnaam", "account_name")}
                  {renderPopupText("G-rekening", "g_iban")}
                  {renderPopupText("G-percentage", "g_percentage")}
                  {renderPopupText("Salesfactor", "sales_factor")}
                  {renderPopupText("Kredietlimiet", "credit_limit")}
                </div>

                <div>
                  <h3>Instellingen</h3>
                  {renderPopupCheck("Factuur digitaal", "invoice_digital")}
                  {renderPopupCheck("Mailinglijst", "mailing_list")}
                  {renderPopupText("Betaalmethode", "payment_method")}

                  <label style={labelStyle}>Opmerking</label>
                  <textarea
                    value={form.remarks || ""}
                    disabled={isReadonlyRole}
                    onChange={(e) => setForm((prev) => ({ ...prev, remarks: e.target.value }))}
                    rows={8}
                    style={{ ...inputStyle, resize: "vertical", minHeight: 150 }}
                  />
                </div>
              </div>

              <div style={{ marginTop: 14 }}>
                <h3>Matching gegevens</h3>

                {matchingRows.length === 0 ? (
                  <div style={{ opacity: 0.7 }}>Geen matching gegevens.</div>
                ) : (
                  <div style={{ display: "grid", gap: 8 }}>
                    {matchingRows.map((m) => (
                      <div
                        key={m.id}
                        style={{
                          border: "1px solid #e2e2e2",
                          borderRadius: 6,
                          padding: 8,
                          fontSize: 13,
                          background: "#fafafa",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                          <b>{m.category || "-"}</b>
                          <span style={{ opacity: 0.7 }}>{m.diploma ? "Diploma" : ""}</span>
                        </div>

                        <div>{m.name || "-"}</div>
                        {m.description && <div style={{ opacity: 0.8, whiteSpace: "pre-wrap" }}>{m.description}</div>}

                        <div style={{ opacity: 0.75 }}>
                          {m.level ? `Niveau: ${m.level}` : ""}
                          {[formatDateNL(m.date_from), formatDateNL(m.date_to)].filter(Boolean).length
                            ? ` · ${[formatDateNL(m.date_from), formatDateNL(m.date_to)].filter(Boolean).join(" t/m ")}`
                            : ""}
                        </div>

                        {m.employee && <div style={{ opacity: 0.75 }}>Werknemer: {m.employee}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
